import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { SiGoogle } from "react-icons/si";
import { FiLoader } from "react-icons/fi";
import { apiFetch } from "../utils/apiFetch";
import { verifyBrowserSession } from "../utils/verifyBrowserSession";
import { saveDisplayUser, takePendingInvitation } from "../utils/authStorage";
import { useAuth } from "../context/AuthContext";
import policies from "../../shared/legalPolicies.json";

let scriptPromise;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const timer = setTimeout(() => { script.remove(); reject(Error("Google could not load. Please try again.")); }, 15000);
    script.src = "https://accounts.google.com/gsi/client"; script.async = true;
    script.onload = () => { clearTimeout(timer); resolve(); };
    script.onerror = () => { clearTimeout(timer); script.remove(); reject(Error("Google could not load. Please try again.")); };
    document.head.appendChild(script);
  }).catch(error => { scriptPromise = null; throw error; });
  return scriptPromise;
}

export default function GoogleSignIn({ mode = "login", onVerified }) {
  const { setUserId, setUserInfo } = useAuth();
  const navigate = useNavigate(), button = useRef(null), request = useRef(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => () => request.current?.abort(), []);
  const start = async () => {
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    setBusy(true); setError(""); setDone(false); setReady(false);
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/auth/google/challenge`, {
        method: "POST", signal: controller.signal, headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, termsAcceptance: { accepted: mode === "signup", version: policies.version } }),
      });
      const challenge = await response.json();
      if (!response.ok) throw Error(challenge.message || "Google sign-in unavailable.");
      await loadGoogle();
      if (controller.signal.aborted) return;
      window.google.accounts.id.initialize({ client_id: challenge.clientId, nonce: challenge.nonce, auto_select: false,
        callback: async ({ credential }) => {
          if (controller.signal.aborted) return;
          setBusy(true); setError("");
          try {
            const result = await apiFetch(`${process.env.API_URL}/api/auth/google/complete`, {
              method: "POST", signal: controller.signal, headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ credential, nonce: challenge.nonce, mode }),
            });
            const data = await result.json();
            if (!result.ok) throw Error(data.message || "Google sign-in failed.");
            if (controller.signal.aborted) return;
            if (mode === "reauth") { onVerified?.(data.googleReauthToken); setDone(true); }
            else if (mode === "link") { setUserInfo(previous => previous ? { ...previous, googleLinked: true } : previous); setDone(true); }
            else {
              const user = await verifyBrowserSession();
              if (controller.signal.aborted) return;
              saveDisplayUser(user); setUserId(user.id); setUserInfo(user);
              const invitation = takePendingInvitation();
              navigate(invitation ? `/join${invitation}` : "/explore", { replace: true });
            }
          } catch (error) { if (!controller.signal.aborted) setError(error.message); }
          finally { if (!controller.signal.aborted) { setBusy(false); setReady(false); } }
        },
      });
      button.current.replaceChildren();
      window.google.accounts.id.renderButton(button.current, { type: "standard", theme: "outline", size: "large", text: mode === "signup" ? "signup_with" : "signin_with", width: Math.min(320, button.current.parentElement.clientWidth) });
      setReady(true);
    } catch (error) { if (!controller.signal.aborted) setError(error.message); }
    finally { if (!controller.signal.aborted) setBusy(false); }
  };
  return <div className="my-4 space-y-3">
    {!ready && <button type="button" disabled={busy} onClick={start} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-[var(--app-border)] bg-[var(--app-panel)] px-3 text-sm font-semibold text-[var(--app-text)] disabled:opacity-50">{busy ? <FiLoader className="animate-spin" /> : <SiGoogle aria-hidden="true" />}{mode === "link" ? "Link Google account" : mode === "reauth" ? "Verify with Google" : "Continue with Google"}</button>}
    <div ref={button} hidden={!ready || busy} />
    {mode === "signup" && <p className="text-xs leading-5 text-[var(--app-secondary)]">By continuing with Google, you agree to the <Link to="/policies/terms" className="underline">Terms</Link> and acknowledge the <Link to="/policies/privacy" className="underline">Privacy</Link> and draft <Link to="/policies/refunds" className="underline">Refund policies</Link>.</p>}
    {ready && busy && <p role="status" className="flex items-center gap-2 text-sm"><FiLoader className="animate-spin" />Verifying Google account…</p>}
    {ready && !busy && <button type="button" onClick={start} className="text-xs underline">Restart Google sign-in</button>}
    {done && <p role="status" className="text-sm">{mode === "link" ? "Google account linked." : "Google account verified. Save your bank details within five minutes."}</p>}
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
  </div>;
}
